import { useState, useEffect, useCallback } from 'react';
import { startOfDay, startOfWeek, startOfMonth, subDays, format } from 'date-fns';
import { useToast } from '@/components/ui/use-toast';
import { CloseMetrics } from '@/services/closeApi/types';
import { useClose } from '@/contexts/CloseContext';
import { CloseApiService } from '@/services/closeApi/closeApiService';
import { 
  isInRateLimitCooldown, 
  isTooSoonForEndpoint, 
  getRemainingCooldownTime,
  formatTimeRemaining 
} from '@/utils/closeApiRateLimiting';

interface UseCloseMetricsOptions {
  refreshInterval?: number;
}

const DEFAULT_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes
const METRICS_ENDPOINT_KEY = 'close_metrics';

// Initialize empty metrics object
const emptyMetricsData = {
  newEntries: { today: 0, thisWeek: 0, thisMonth: 0, total: 0, byDay: [0, 0, 0, 0, 0, 0, 0] },
  callAttempts: { today: 0, thisWeek: 0, thisMonth: 0, total: 0, byDay: [0, 0, 0, 0, 0, 0, 0] },
  callsReached: { today: 0, thisWeek: 0, thisMonth: 0, total: 0, byDay: [0, 0, 0, 0, 0, 0, 0] },
  callDuration: { today: 0, thisWeek: 0, thisMonth: 0, total: 0, byDay: [0, 0, 0, 0, 0, 0, 0] },
  appointments: { today: 0, thisWeek: 0, thisMonth: 0, total: 0, byDay: [0, 0, 0, 0, 0, 0, 0] },
  completedAppointments: { today: 0, thisWeek: 0, thisMonth: 0, total: 0, byDay: [0, 0, 0, 0, 0, 0, 0] },
  agreedAppointments: { today: 0, thisWeek: 0, thisMonth: 0, total: 0, byDay: [0, 0, 0, 0, 0, 0, 0] },
  closedDeals: { today: 0, thisWeek: 0, thisMonth: 0, total: 0, byDay: [0, 0, 0, 0, 0, 0, 0] },
  revenue: { today: 0, thisWeek: 0, thisMonth: 0, total: 0, byDay: [0, 0, 0, 0, 0, 0, 0] },
};

export function useCloseMetrics(
  options: UseCloseMetricsOptions = {}
): CloseMetrics & {
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  nextRefreshTime: number | null;
  refreshMetrics: () => Promise<void>;
  remainingCooldown: number;
} {
  const { refreshInterval = DEFAULT_REFRESH_INTERVAL } = options;
  const [metrics, setMetrics] = useState<CloseMetrics>(emptyMetricsData);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextRefreshTime, setNextRefreshTime] = useState<number | null>(null);
  const [remainingCooldown, setRemainingCooldown] = useState(0);
  const { toast } = useToast();
  const { isConnected, apiKey } = useClose();
  
  // Function to update remaining cooldown
  const updateRemainingCooldown = useCallback(() => {
    if (isInRateLimitCooldown()) {
      const remaining = getRemainingCooldownTime();
      setRemainingCooldown(remaining);
      return remaining;
    } else if (isTooSoonForEndpoint(METRICS_ENDPOINT_KEY)) {
      // Check if the last request was too recent
      const now = Date.now();
      const lastRequest = localStorage.getItem('close_metrics_last_request');
      if (lastRequest) {
        const elapsed = now - parseInt(lastRequest, 10);
        const timeLeft = Math.ceil((5 * 60 * 1000 - elapsed) / 1000);
        if (timeLeft > 0) {
          setRemainingCooldown(timeLeft);
          return timeLeft;
        }
      }
    }
    setRemainingCooldown(0);
    return 0;
  }, []);

  // Calculate metrics from API data
  const calculateMetricsFromApiData = useCallback(async () => {
    if (!isConnected || !apiKey) {
      setIsLoading(false);
      return;
    }
    
    // Check for rate limiting first
    if (isInRateLimitCooldown() || isTooSoonForEndpoint(METRICS_ENDPOINT_KEY)) {
      const remaining = updateRemainingCooldown();
      if (remaining > 0) {
        const formattedTime = formatTimeRemaining(remaining);
        throw new Error(`Zu viele Abfragen. Bitte warten Sie ${formattedTime} für die nächste Aktualisierung.`);
      }
    }

    try {
      // Store last request time
      localStorage.setItem('close_metrics_last_request', Date.now().toString());
      
      // Fetch data from Close API
      const activities = await CloseApiService.getActivities(apiKey);
      const leads = await CloseApiService.getLeads(apiKey);
      const opportunities = await CloseApiService.getOpportunities(apiKey);
      const tasks = await CloseApiService.getTasks(apiKey);

      const today = new Date();
      const todayStr = format(today, 'yyyy-MM-dd');
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const monthStart = startOfMonth(today);
      const monthStartStr = format(monthStart, 'yyyy-MM-dd');

      // Initialize new metrics structure
      const newMetrics: CloseMetrics = JSON.parse(JSON.stringify(emptyMetricsData));

      // Process leads for new entries
      leads.forEach(lead => {
        const createdDate = new Date(lead.created_at);
        const createdDateStr = format(createdDate, 'yyyy-MM-dd');
        
        // Increment appropriate counters
        if (createdDateStr === todayStr) {
          newMetrics.newEntries.today++;
        }
        
        if (createdDate >= weekStart) {
          newMetrics.newEntries.thisWeek++;
        }
        
        if (createdDate >= monthStart) {
          newMetrics.newEntries.thisMonth++;
        }
        
        newMetrics.newEntries.total++;
      });

      // Process activities for calls
      activities.forEach(activity => {
        if (activity.type === 'call') {
          const createdDate = new Date(activity.created_at);
          const createdDateStr = format(createdDate, 'yyyy-MM-dd');
          
          // Calls attempted
          if (createdDateStr === todayStr) {
            newMetrics.callAttempts.today++;
          }
          
          if (createdDate >= weekStart) {
            newMetrics.callAttempts.thisWeek++;
          }
          
          if (createdDate >= monthStart) {
            newMetrics.callAttempts.thisMonth++;
          }
          
          newMetrics.callAttempts.total++;
          
          // Calls reached (assuming any call with duration > 30s was reached)
          const duration = (activity as any).duration || 0;
          if (duration > 30) {
            if (createdDateStr === todayStr) {
              newMetrics.callsReached.today++;
            }
            
            if (createdDate >= weekStart) {
              newMetrics.callsReached.thisWeek++;
            }
            
            if (createdDate >= monthStart) {
              newMetrics.callsReached.thisMonth++;
            }
            
            newMetrics.callsReached.total++;
            
            // Call duration (in minutes)
            const durationMinutes = Math.round(duration / 60);
            if (createdDateStr === todayStr) {
              newMetrics.callDuration.today += durationMinutes;
            }
            
            if (createdDate >= weekStart) {
              newMetrics.callDuration.thisWeek += durationMinutes;
            }
            
            if (createdDate >= monthStart) {
              newMetrics.callDuration.thisMonth += durationMinutes;
            }
            
            newMetrics.callDuration.total += durationMinutes;
          }
        }
      });

      // Process tasks for appointments
      tasks.forEach(task => {
        const createdDate = new Date(task.created_at);
        const createdDateStr = format(createdDate, 'yyyy-MM-dd');
        
        // Calendar appointments
        const isAppointment = task.text.toLowerCase().includes('meeting') || 
                              task.text.toLowerCase().includes('appointment');
        
        if (isAppointment) {
          if (createdDateStr === todayStr) {
            newMetrics.appointments.today++;
          }
          
          if (createdDate >= weekStart) {
            newMetrics.appointments.thisWeek++;
          }
          
          if (createdDate >= monthStart) {
            newMetrics.appointments.thisMonth++;
          }
          
          newMetrics.appointments.total++;
          
          // Completed appointments
          if (task.is_complete) {
            if (createdDateStr === todayStr) {
              newMetrics.completedAppointments.today++;
            }
            
            if (createdDate >= weekStart) {
              newMetrics.completedAppointments.thisWeek++;
            }
            
            if (createdDate >= monthStart) {
              newMetrics.completedAppointments.thisMonth++;
            }
            
            newMetrics.completedAppointments.total++;
          }
          
          // Agreed appointments
          const isAgreed = task.text.toLowerCase().includes('agreed') || 
                          task.text.toLowerCase().includes('confirmed');
          
          if (isAgreed) {
            if (createdDateStr === todayStr) {
              newMetrics.agreedAppointments.today++;
            }
            
            if (createdDate >= weekStart) {
              newMetrics.agreedAppointments.thisWeek++;
            }
            
            if (createdDate >= monthStart) {
              newMetrics.agreedAppointments.thisMonth++;
            }
            
            newMetrics.agreedAppointments.total++;
          }
        }
      });

      // Process opportunities for deals and revenue
      opportunities.forEach(opp => {
        if (opp.status_label.toLowerCase().includes('won')) {
          const updatedDate = new Date(opp.updated_at);
          const updatedDateStr = format(updatedDate, 'yyyy-MM-dd');
          
          // Closed deals
          if (updatedDateStr === todayStr) {
            newMetrics.closedDeals.today++;
          }
          
          if (updatedDate >= weekStart) {
            newMetrics.closedDeals.thisWeek++;
          }
          
          if (updatedDate >= monthStart) {
            newMetrics.closedDeals.thisMonth++;
          }
          
          newMetrics.closedDeals.total++;
          
          // Revenue
          const revenue = opp.value;
          if (updatedDateStr === todayStr) {
            newMetrics.revenue.today += revenue;
          }
          
          if (updatedDate >= weekStart) {
            newMetrics.revenue.thisWeek += revenue;
          }
          
          if (updatedDate >= monthStart) {
            newMetrics.revenue.thisMonth += revenue;
          }
          
          newMetrics.revenue.total += revenue;
        }
      });

      // Fill byDay arrays for the last 7 days
      const last7Days = Array.from({ length: 7 }, (_, i) => subDays(today, 6 - i));
      
      last7Days.forEach((date, index) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        
        newMetrics.newEntries.byDay[index] = leads.filter(
          lead => format(new Date(lead.created_at), 'yyyy-MM-dd') === dateStr
        ).length;
        
        const dailyCalls = activities.filter(
          act => act.type === 'call' && format(new Date(act.created_at), 'yyyy-MM-dd') === dateStr
        );
        
        newMetrics.callAttempts.byDay[index] = dailyCalls.length;
        
        const reachedCalls = dailyCalls.filter(call => (call as any).duration > 30);
        newMetrics.callsReached.byDay[index] = reachedCalls.length;
        
        newMetrics.callDuration.byDay[index] = reachedCalls.reduce(
          (sum, call) => sum + Math.round(((call as any).duration || 0) / 60), 0
        );
        
        const dailyAppointments = tasks.filter(
          task => (task.text.toLowerCase().includes('meeting') || 
                  task.text.toLowerCase().includes('appointment')) &&
                  format(new Date(task.created_at), 'yyyy-MM-dd') === dateStr
        );
        
        newMetrics.appointments.byDay[index] = dailyAppointments.length;
        
        newMetrics.completedAppointments.byDay[index] = dailyAppointments.filter(
          task => task.is_complete
        ).length;
        
        newMetrics.agreedAppointments.byDay[index] = dailyAppointments.filter(
          task => task.text.toLowerCase().includes('agreed') || 
                 task.text.toLowerCase().includes('confirmed')
        ).length;
        
        const dailyWonOpps = opportunities.filter(
          opp => opp.status_label.toLowerCase().includes('won') &&
                format(new Date(opp.updated_at), 'yyyy-MM-dd') === dateStr
        );
        
        newMetrics.closedDeals.byDay[index] = dailyWonOpps.length;
        
        newMetrics.revenue.byDay[index] = dailyWonOpps.reduce(
          (sum, opp) => sum + opp.value, 0
        );
      });

      setMetrics(newMetrics);
      
      return newMetrics;
    } catch (err) {
      console.error("Error calculating metrics from API data:", err);
      
      // Improved network error handling
      if (err instanceof Error && err.message.includes('Netzwerkfehler')) {
        throw new Error('Netzwerkfehler beim Zugriff auf die Close API. Bitte überprüfen Sie Ihre Internetverbindung und API-Einstellungen.');
      }
      
      throw err;
    }
  }, [apiKey, isConnected, updateRemainingCooldown]);

  // Fetch metrics from Close API directly
  const fetchMetrics = useCallback(async () => {
    if (!isConnected || !apiKey) {
      setIsLoading(false);
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      // Check for cooldown/rate limiting first
      const cooldown = updateRemainingCooldown();
      if (cooldown > 0) {
        const formattedTime = formatTimeRemaining(cooldown);
        throw new Error(`Zu viele Abfragen. Bitte warten Sie ${formattedTime} für die nächste Aktualisierung.`);
      }
      
      console.log("Fetching Close CRM metrics directly from API");
      
      // Use cached metrics if available
      const cachedMetrics = localStorage.getItem('close_metrics_cache');
      if (cachedMetrics) {
        const parsed = JSON.parse(cachedMetrics);
        const cacheTime = localStorage.getItem('close_metrics_cache_time');
        
        if (cacheTime) {
          const cacheAge = Date.now() - parseInt(cacheTime, 10);
          console.log(`Metrics cache is ${Math.round(cacheAge/1000)} seconds old`);
          
          // Use cache if it's less than 5 minutes old
          if (cacheAge < 5 * 60 * 1000) {
            console.log("Using cached metrics (less than 5 minutes old)");
            setMetrics(parsed);
            setNextRefreshTime(Date.now() + refreshInterval);
            setIsLoading(false);
            setIsFetching(false);
            return;
          }
        }
      }
      
      // Calculate metrics from API data
      const newMetrics = await calculateMetricsFromApiData();
      
      // Cache the metrics
      if (newMetrics) {
        localStorage.setItem('close_metrics_cache', JSON.stringify(newMetrics));
        localStorage.setItem('close_metrics_cache_time', Date.now().toString());
      }
      
      // Set next refresh time
      setNextRefreshTime(Date.now() + refreshInterval);
    } catch (err) {
      console.error("Error fetching Close metrics:", err);
      
      let errorMessage = "Failed to fetch metrics";
      
      // Improved error messages for different scenarios
      if (err instanceof Error) {
        if (err.message.includes('Netzwerkfehler')) {
          errorMessage = err.message;
        } else if (err.message.includes('Zu viele Abfragen')) {
          errorMessage = err.message;
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      
      toast({
        title: "Fehler beim Laden der Metriken",
        description: errorMessage,
        variant: "destructive",
      });
      
      // Update cooldown display if this was a rate limit error
      updateRemainingCooldown();
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [apiKey, calculateMetricsFromApiData, isConnected, refreshInterval, toast, updateRemainingCooldown]);

  // Refresh metrics function exposed to consumers
  const refreshMetrics = async () => {
    try {
      // Check cooldown first
      const cooldown = updateRemainingCooldown();
      if (cooldown > 0) {
        const formattedTime = formatTimeRemaining(cooldown);
        toast({
          title: "Zu viele Anfragen",
          description: `Bitte warten Sie ${formattedTime} bis zur nächsten Aktualisierung.`,
          variant: "destructive",
        });
        return;
      }
      
      setIsFetching(true);
      await fetchMetrics();
      
      toast({
        title: "Metriken aktualisiert",
        description: "Die Close CRM Metriken wurden erfolgreich aktualisiert.",
      });
      
      // Set next refresh time
      setNextRefreshTime(Date.now() + refreshInterval);
    } catch (err) {
      console.error("Failed to refresh metrics:", err);
      setError(err instanceof Error ? err.message : "Failed to refresh metrics");
      
      toast({
        title: "Aktualisierung fehlgeschlagen",
        description: err instanceof Error ? err.message : "Unbekannter Fehler",
        variant: "destructive",
      });
      
      // Update cooldown display
      updateRemainingCooldown();
    } finally {
      setIsFetching(false);
    }
  };

  // Set up initial fetch and refresh interval
  useEffect(() => {
    // On mount, check if we have cached metrics
    const cachedMetrics = localStorage.getItem('close_metrics_cache');
    if (cachedMetrics) {
      try {
        const parsed = JSON.parse(cachedMetrics);
        setMetrics(parsed);
        setIsLoading(false);
        
        // If cache is more than 5 minutes old, fetch fresh data
        const cacheTime = localStorage.getItem('close_metrics_cache_time');
        if (cacheTime) {
          const cacheAge = Date.now() - parseInt(cacheTime, 10);
          if (cacheAge > 5 * 60 * 1000) {
            console.log("Cache is older than 5 minutes, fetching fresh data");
            fetchMetrics();
          } else {
            console.log("Using cached metrics, still fresh");
          }
        }
      } catch (e) {
        console.error("Error parsing cached metrics:", e);
        fetchMetrics();
      }
    } else {
      fetchMetrics();
    }
    
    // Update cooldown status every second
    const cooldownTimer = setInterval(() => {
      updateRemainingCooldown();
    }, 1000);
    
    // Set up refresh interval
    const intervalId = setInterval(() => {
      if (isConnected && apiKey && !isInRateLimitCooldown()) {
        console.log("Auto-refreshing Close CRM metrics");
        fetchMetrics();
      }
    }, refreshInterval);
    
    return () => {
      clearInterval(intervalId);
      clearInterval(cooldownTimer);
    };
  }, [fetchMetrics, isConnected, apiKey, refreshInterval, updateRemainingCooldown]);

  return {
    ...metrics,
    isLoading,
    isFetching,
    error,
    nextRefreshTime,
    refreshMetrics,
    remainingCooldown
  };
}
